import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, CheckCircle, Edit2 } from "lucide-react";
import { DataTable, DataTableColumn } from "@/components/DataTable";
import { getFinancialTransactions, createFinancialTransaction, updateFinancialTransaction, deleteFinancialTransaction } from "@/lib/api-financial";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { formatBRL } from "@/lib/utils";
import { resolveFinancialCategoryLabel, useFinancialCategories } from "@/hooks/useFinancialCategories";

const statusColors: Record<string, string> = {
  pending: "secondary",
  confirmed: "default",
  paid: "default",
  cancelled: "destructive",
};

export default function ProducerAccountsReceivable({ producerId }: { producerId: string }) {
  const { t } = useTranslation("producerFinancial");
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategoryLabel, setNewCategoryLabel] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { categories, addCategory, removeCategory } = useFinancialCategories(producerId, "receivable");
  const [form, setForm] = useState({
    description: "",
    amount: "",
    category: "ticket_sale",
    due_date: "",
    notes: "",
  });

  useEffect(() => {
    if (!categories.some((category) => category.value === form.category)) {
      setForm((previous) => ({ ...previous, category: categories[0]?.value || "ticket_sale" }));
    }
  }, [categories, form.category]);

  const categoryOptionsForCreate = useMemo(() => {
    if (categories.some((category) => category.value === form.category)) return categories;
    return [...categories, { value: form.category, label: resolveFinancialCategoryLabel(form.category, categories) }];
  }, [categories, form.category]);

  const { data: transactions = [], isLoading } = useQuery({
    queryKey: ["financial-receivable", producerId, statusFilter],
    queryFn: () => getFinancialTransactions(producerId, {
      type: "receivable",
      status: statusFilter === "all" ? undefined : statusFilter,
    }),
    staleTime: 30_000,
  });

  const createMut = useMutation({
    mutationFn: () => createFinancialTransaction({
      producer_id: producerId,
      type: "receivable",
      category: form.category,
      description: form.description,
      amount: parseFloat(form.amount) || 0,
      due_date: form.due_date || undefined,
      notes: form.notes || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-receivable"] });
      queryClient.invalidateQueries({ queryKey: ["cash-flow"] });
      setShowCreate(false);
      setForm({ description: "", amount: "", category: "ticket_sale", due_date: "", notes: "" });
      toast({ title: t("accountsReceivable.toast.created") });
    },
    onError: (err: any) => toast({ title: t("accountsReceivable.toast.error"), description: err.message, variant: "destructive" }),
  });

  const confirmMut = useMutation({
    mutationFn: (id: string) => updateFinancialTransaction(id, { status: "paid", paid_at: new Date().toISOString() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-receivable"] });
      queryClient.invalidateQueries({ queryKey: ["cash-flow"] });
      toast({ title: t("accountsReceivable.toast.confirmed") });
    },
  });

  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({ description: "", amount: "", category: "", due_date: "", notes: "" });

  const editMut = useMutation({
    mutationFn: () => updateFinancialTransaction(editingRow!.id, {
      description: editForm.description,
      amount: parseFloat(editForm.amount) || 0,
      category: editForm.category,
      due_date: editForm.due_date || undefined,
      notes: editForm.notes || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-receivable"] });
      queryClient.invalidateQueries({ queryKey: ["cash-flow"] });
      setEditingRow(null);
      toast({ title: t("accountsReceivable.toast.updated") });
    },
    onError: (err: any) => toast({ title: t("accountsReceivable.toast.error"), description: err.message, variant: "destructive" }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteFinancialTransaction(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-receivable"] });
      queryClient.invalidateQueries({ queryKey: ["cash-flow"] });
      toast({ title: t("accountsReceivable.toast.deleted") });
    },
  });

  const columns: DataTableColumn<any>[] = [
    { key: "description", header: t("accountsReceivable.table.description"), render: (row) => <span className="font-medium">{row.description}</span> },
    { key: "category", header: t("accountsReceivable.table.category"), render: (row) => <span className="text-sm">{resolveFinancialCategoryLabel(row.category, categories)}</span> },
    { key: "amount", header: t("accountsReceivable.table.amount"), render: (row) => <span className="font-semibold text-primary">{formatBRL(row.amount)}</span> },
    { key: "due_date", header: t("accountsReceivable.table.dueDate"), render: (row) => row.due_date ? format(new Date(row.due_date), "dd/MM/yyyy") : "—" },
    { key: "status", header: t("accountsReceivable.table.status"), render: (row) => <Badge variant={statusColors[row.status] as any}>{t(`accountsReceivable.status.${row.status}`)}</Badge> },
    {
      key: "actions", header: "", render: (row) => (
        <div className="flex gap-1">
          {row.status === "pending" && (
            <Button size="icon" variant="ghost" onClick={() => confirmMut.mutate(row.id)} title={t("accountsReceivable.table.confirmReceipt")}>
              <CheckCircle className="h-4 w-4 text-primary" />
            </Button>
          )}
          <Button size="icon" variant="ghost" onClick={() => { setEditingRow(row); setEditForm({ description: row.description, amount: String(row.amount), category: row.category, due_date: row.due_date ? row.due_date.slice(0, 10) : "", notes: row.notes || "" }); }} title={t("accountsReceivable.table.edit")}>
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-foreground" onClick={() => deleteMut.mutate(row.id)} title={t("accountsReceivable.table.delete")}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const total = transactions.reduce((s: number, t: any) => s + Number(t.amount), 0);

  const handleAddCategory = async () => {
    const result = await addCategory(newCategoryLabel);
    if (!result.ok) {
      toast({ title: t("accountsReceivable.toast.categoryInvalid"), description: result.reason, variant: "destructive" });
      return;
    }

    setNewCategoryLabel("");
    setForm((previous) => ({ ...previous, category: result.value }));
    toast({ title: t("accountsReceivable.toast.categoryAdded") });
  };

  const handleRemoveCategory = async (value: string) => {
    const result = await removeCategory(value);
    if (!result.ok) {
      toast({ title: t("accountsReceivable.toast.removeFailed"), description: result.reason, variant: "destructive" });
      return;
    }

    if (form.category === value) {
      setForm((previous) => ({ ...previous, category: result.fallback || "ticket_sale" }));
    }

    toast({ title: t("accountsReceivable.toast.categoryRemoved") });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("accountsReceivable.status.all")}</SelectItem>
              <SelectItem value="pending">{t("accountsReceivable.status.pending")}</SelectItem>
              <SelectItem value="confirmed">{t("accountsReceivable.status.confirmed")}</SelectItem>
              <SelectItem value="paid">{t("accountsReceivable.status.paid")}</SelectItem>
              <SelectItem value="cancelled">{t("accountsReceivable.status.cancelled")}</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">Total: <strong className="text-primary">{formatBRL(total)}</strong></span>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          <Button size="sm" variant="outline" className="flex-1 sm:flex-none" onClick={() => setShowCategoryManager(true)}>Categorias</Button>
          <Button size="sm" className="flex-1 sm:flex-none" onClick={() => setShowCreate(true)}><Plus className="h-4 w-4 mr-1" /> {t("accountsReceivable.newReceivable")}</Button>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <DataTable columns={columns} data={transactions} isLoading={isLoading} emptyMessage={t("accountsReceivable.table.empty")} />
      </div>

      {/* Mobile Card List View */}
      <div className="md:hidden space-y-2.5">
        {isLoading ? (
          <div className="p-4 text-center text-xs text-muted-foreground">{t("accountsReceivable.table.loading")}</div>
        ) : transactions.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground border border-dashed border-border/40 rounded-2xl">
            {t("accountsReceivable.table.empty")}
          </div>
        ) : (
          transactions.map((tx: any) => (
            <div key={tx.id} className="rounded-2xl border border-border/40 bg-card p-4 space-y-2.5 shadow-none">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className="text-xs font-semibold text-foreground">{tx.description}</h4>
                  <p className="text-[10px] text-muted-foreground">
                    {resolveFinancialCategoryLabel(tx.category, categories)} {tx.due_date ? `· Venc.: ${format(new Date(tx.due_date), "dd/MM/yyyy")}` : ""}
                  </p>
                </div>
                <Badge variant={statusColors[tx.status] as any} className="text-[10px] shrink-0 font-normal">
                  {t(`accountsReceivable.status.${tx.status}`)}
                </Badge>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-border/30 text-xs">
                <span className="text-sm font-semibold text-primary tabular-nums">{formatBRL(tx.amount)}</span>
                <div className="flex items-center gap-1">
                  {tx.status === "pending" && (
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-xs text-primary font-medium" onClick={() => confirmMut.mutate(tx.id)}>
                      <CheckCircle className="h-3.5 w-3.5 mr-1" /> {t("accountsReceivable.table.receive")}
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditingRow(tx); setEditForm({ description: tx.description, amount: String(tx.amount), category: tx.category, due_date: tx.due_date ? tx.due_date.slice(0, 10) : "", notes: tx.notes || "" }); }}>
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={() => deleteMut.mutate(tx.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent mobilePresentation="fullscreen">
          <DialogHeader>
            <DialogTitle>{t("accountsReceivable.modal.createTitle")}</DialogTitle>
            <DialogDescription>{t("accountsReceivable.modal.createDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>{t("accountsReceivable.modal.descriptionLabel")}</Label><Input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} placeholder={t("accountsReceivable.modal.descriptionPlaceholder")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("accountsReceivable.modal.amountLabel")}</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm(p => ({ ...p, amount: e.target.value }))} /></div>
              <div>
                <Label>{t("accountsReceivable.modal.categoryLabel")}</Label>
                <Select value={form.category} onValueChange={(v) => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categoryOptionsForCreate.map((category) => (
                      <SelectItem key={category.value} value={category.value}>{category.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>{t("accountsReceivable.modal.dueDateLabel")}</Label><Input type="date" value={form.due_date} onChange={(e) => setForm(p => ({ ...p, due_date: e.target.value }))} /></div>
            <div><Label>{t("accountsReceivable.modal.notesLabel")}</Label><Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("accountsReceivable.modal.cancel")}</Button>
            <Button onClick={() => createMut.mutate()} disabled={!form.description || !form.amount || createMut.isPending}>{t("accountsReceivable.modal.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingRow} onOpenChange={(open) => { if (!open) setEditingRow(null); }}>
        <DialogContent mobilePresentation="fullscreen">
          <DialogHeader>
            <DialogTitle>{t("accountsReceivable.modal.editTitle")}</DialogTitle>
            <DialogDescription>{t("accountsReceivable.modal.editDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>{t("accountsReceivable.modal.descriptionLabel")}</Label><Input value={editForm.description} onChange={(e) => setEditForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("accountsReceivable.modal.amountLabel")}</Label><Input type="number" step="0.01" value={editForm.amount} onChange={(e) => setEditForm(p => ({ ...p, amount: e.target.value }))} /></div>
              <div>
                <Label>{t("accountsReceivable.modal.categoryLabel")}</Label>
                <Select value={editForm.category} onValueChange={(v) => setEditForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.value} value={category.value}>{category.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>{t("accountsReceivable.modal.dueDateLabel")}</Label><Input type="date" value={editForm.due_date} onChange={(e) => setEditForm(p => ({ ...p, due_date: e.target.value }))} /></div>
            <div><Label>{t("accountsReceivable.modal.notesLabel")}</Label><Input value={editForm.notes} onChange={(e) => setEditForm(p => ({ ...p, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingRow(null)}>{t("accountsReceivable.modal.cancel")}</Button>
            <Button onClick={() => editMut.mutate()} disabled={!editForm.description || !editForm.amount || editMut.isPending}>{t("accountsReceivable.modal.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCategoryManager} onOpenChange={setShowCategoryManager}>
        <DialogContent mobilePresentation="fullscreen">
          <DialogHeader>
            <DialogTitle>{t("accountsReceivable.modal.categoryManagerTitle")}</DialogTitle>
            <DialogDescription>{t("accountsReceivable.modal.categoryManagerDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newCategoryLabel}
                onChange={(e) => setNewCategoryLabel(e.target.value)}
                placeholder={t("accountsReceivable.modal.categoryPlaceholder")}
              />
              <Button onClick={handleAddCategory} disabled={!newCategoryLabel.trim()}>{t("accountsReceivable.modal.add")}</Button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {categories.map((category) => (
                <div key={category.value} className="flex items-center justify-between rounded-md border p-2">
                  <span className="text-sm">{category.label}</span>
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleRemoveCategory(category.value)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryManager(false)}>{t("accountsReceivable.modal.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
